import React, { useEffect, useState } from 'react';
// Context
import { InventariosProvider, useInventarios } from './InventariosContext';
// Material-UI
import { DataGrid, esES } from '@material-ui/data-grid';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Modal from '@material-ui/core/Modal';
import Snackbar from '@material-ui/core/Snackbar';
import TextField from '@material-ui/core/TextField';
// Icons
import { Pencil, Trash, X } from 'tabler-icons-react';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
// Styles
import '../../styles/inventarios.css';
import '../../App.css';

const InventariosWrapper = () => (
    <InventariosProvider>
        <Inventarios />
    </InventariosProvider>
);

export default InventariosWrapper;

// ── Columns ──────────────────────────────────────────────────────────────────

const tableColumns = [
    { field: 'sku', headerName: 'SKU', flex: 0.8, minWidth: 100 },
    { field: 'nombre', headerName: 'Nombre', flex: 2, minWidth: 180 },
    {
        field: 'existe',
        headerName: 'Cantidad',
        flex: 0.8,
        minWidth: 100,
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'unidadInv',
        headerName: 'Medida',
        flex: 0.8,
        minWidth: 100,
        align: 'center',
        headerAlign: 'center',
    },
];

// ── Dialogs ───────────────────────────────────────────────────────────────────

function AlmacenFormModal({ open, onClose, isEdit }) {
    const { editingAlmacen, crearAlmacen, editarAlmacen, saving } = useInventarios();

    const target = isEdit ? editingAlmacen : null;

    const [nombre, setNombre] = useState('');
    const [tipo, setTipo] = useState('');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            setNombre(isEdit && target ? target.nombreAlmacen : '');
            setTipo(isEdit && target ? String(target.tipoAlmacen) : '');
            setErrors({});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const validate = () => {
        const e = {};
        if (!nombre.trim()) e.nombre = 'El nombre es requerido';
        if (!tipo || isNaN(Number(tipo)) || Number(tipo) < 1) e.tipo = 'Tipo inválido';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        if (isEdit) {
            editarAlmacen(target.almacen, nombre.trim(), Number(tipo));
        } else {
            crearAlmacen(nombre.trim(), Number(tipo));
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <div className="inv-modal">
                <div className="inv-modal-header">
                    <h2 className="inv-modal-title">{isEdit ? 'Editar almacén' : 'Nuevo almacén'}</h2>
                    <IconButton size="small" onClick={onClose}>
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </div>
                <div className="inv-modal-body">
                    <TextField
                        label="Nombre del almacén"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        error={!!errors.nombre}
                        helperText={errors.nombre}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label="Tipo de almacén"
                        type="number"
                        value={tipo}
                        onChange={(e) => setTipo(e.target.value)}
                        error={!!errors.tipo}
                        helperText={errors.tipo}
                        inputProps={{ min: 1 }}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </div>
                <div className="inv-modal-footer">
                    <Button onClick={onClose} disabled={saving} className="inv-modal-cancel" variant="outlined">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={saving}
                        className="inv-modal-submit"
                    >
                        {saving ? <CircularProgress size="1rem" /> : isEdit ? 'Guardar' : 'Crear'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function ConfirmDeleteModal({ open, onClose }) {
    const { editingAlmacen, eliminarAlmacen, saving } = useInventarios();

    return (
        <Modal open={open} onClose={onClose}>
            <div className="inv-modal">
                <div className="inv-modal-header">
                    <h2 className="inv-modal-title inv-modal-title--danger">Archivar almacén</h2>
                    <IconButton size="small" onClick={onClose}>
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </div>
                <div className="inv-modal-body">
                    <p className="inv-modal-desc">
                        ¿Estás seguro de archivar <strong>{editingAlmacen?.nombreAlmacen}</strong>?
                        Esta acción no es posible si el almacén tiene existencias de productos.
                    </p>
                </div>
                <div className="inv-modal-footer">
                    <Button onClick={onClose} disabled={saving} className="inv-modal-cancel" variant="outlined">
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => eliminarAlmacen(editingAlmacen?.almacen)}
                        variant="contained"
                        disabled={saving}
                        className="inv-modal-submit inv-modal-submit--danger"
                    >
                        {saving ? <CircularProgress size="1rem" /> : 'Archivar'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

// ── Main view ─────────────────────────────────────────────────────────────────

function Inventarios() {
    const {
        almacenes,
        selectedAlmacen, setSelectedAlmacen,
        existencias,
        skuFilter, setSkuFilter,
        loadingAlmacenes, loadingExistencias,
        snackbar, closeSnackbar,
        dialogCreate, setDialogCreate,
        dialogEdit, setDialogEdit,
        dialogDelete, setDialogDelete,
        setEditingAlmacen,
        getAlmacenes, getExistencias,
    } = useInventarios();

    const [searchText, setSearchText] = useState('');
    const [rows, setRows] = useState([]);

    useEffect(() => { getAlmacenes(); }, [getAlmacenes]);

    useEffect(() => {
        setRows(existencias);
        setSearchText('');
    }, [existencias]);

    const handleAlmacenClick = (item) => {
        setSelectedAlmacen(item);
        setSkuFilter('');
        getExistencias(item.almacen, '');
    };

    const handleSkuSearch = (value) => {
        setSkuFilter(value);
        if (selectedAlmacen) getExistencias(selectedAlmacen.almacen, value);
    };

    const handleLocalSearch = (value) => {
        setSearchText(value);
        if (!value) { setRows(existencias); return; }
        const re = new RegExp(value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'), 'i');
        setRows(existencias.filter((row) =>
            Object.keys(row).some((f) => re.test(row[f]?.toString() ?? ''))
        ));
    };

    const handleEdit = (e, item) => {
        e.stopPropagation();
        setEditingAlmacen(item);
        setDialogEdit(true);
    };

    const handleDelete = (e, item) => {
        e.stopPropagation();
        setEditingAlmacen(item);
        setDialogDelete(true);
    };

    return (
        <div className="inv-page">
            {/* ── Header ── */}
            <div className="inv-header">
                <h1 className="titulo">Inventarios</h1>
                <p className="desc">
                    Gestiona los almacenes y consulta las existencias de productos por ubicación.
                </p>
            </div>

            {/* ── Body ── */}
            <div className="inv-body">
                {/* Panel almacenes */}
                <div className="inv-panel-almacenes">
                    <div className="inv-card">
                        <div className="inv-card-toolbar">
                            <h2 className="inv-card-title">Almacenes</h2>
                        </div>
                        <div className="inv-almacenes-list">
                            {loadingAlmacenes ? (
                                <div className="inv-loading">
                                    <CircularProgress size="1.5rem" />
                                </div>
                            ) : (
                                <>
                                    {almacenes.map((item) => (
                                        <div
                                            key={item.almacen}
                                            className={`inv-almacen-item${selectedAlmacen?.almacen === item.almacen ? ' inv-almacen-item--selected' : ''}`}
                                            onClick={() => handleAlmacenClick(item)}
                                        >
                                            <span className="inv-almacen-name">{item.nombreAlmacen}</span>
                                            <div className="inv-almacen-actions">
                                                <IconButton
                                                    size="small"
                                                    className="inv-almacen-edit-btn"
                                                    onClick={(e) => handleEdit(e, item)}
                                                >
                                                    <Pencil size={14} strokeWidth={1.75} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    className="inv-almacen-delete-btn"
                                                    onClick={(e) => handleDelete(e, item)}
                                                >
                                                    <Trash size={14} strokeWidth={1.75} />
                                                </IconButton>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="inv-almacen-add">
                                        <button className="inv-add-btn" onClick={() => setDialogCreate(true)}>
                                            <span className="inv-add-icon">+</span>
                                            <span>Nuevo almacén</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Panel existencias */}
                <div className="inv-panel-main">
                    <div className="inv-card">
                        <div className="inv-card-toolbar">
                            <h2 className="inv-card-title">
                                {selectedAlmacen ? selectedAlmacen.nombreAlmacen : 'Selecciona un almacén'}
                            </h2>
                            {selectedAlmacen && (
                                <div className="inv-toolbar-filters">
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        value={skuFilter}
                                        onChange={(e) => handleSkuSearch(e.target.value)}
                                        placeholder="Buscar por SKU..."
                                        className="inv-search-field"
                                        InputProps={{
                                            startAdornment: <SearchIcon fontSize="small" />,
                                            endAdornment: skuFilter ? (
                                                <IconButton size="small" onClick={() => handleSkuSearch('')}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            ) : null,
                                        }}
                                    />
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        value={searchText}
                                        onChange={(e) => handleLocalSearch(e.target.value)}
                                        placeholder="Filtrar tabla..."
                                        className="inv-search-field"
                                        InputProps={{
                                            startAdornment: <SearchIcon fontSize="small" />,
                                            endAdornment: searchText ? (
                                                <IconButton size="small" onClick={() => handleLocalSearch('')}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            ) : null,
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="inv-table-wrapper">
                            {!selectedAlmacen ? (
                                <div className="inv-empty">Selecciona un almacén para ver sus existencias</div>
                            ) : loadingExistencias ? (
                                <div className="inv-empty">
                                    <CircularProgress size="1.5rem" />
                                </div>
                            ) : (
                                <DataGrid
                                    disableColumnFilter
                                    disableDensitySelector
                                    columns={tableColumns}
                                    localeText={esES.props.MuiDataGrid.localeText}
                                    rows={rows}
                                    hideFooterSelectedRowCount
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Modals ── */}
            <AlmacenFormModal open={dialogCreate} onClose={() => setDialogCreate(false)} isEdit={false} />
            <AlmacenFormModal open={dialogEdit} onClose={() => setDialogEdit(false)} isEdit />
            <ConfirmDeleteModal open={dialogDelete} onClose={() => setDialogDelete(false)} />

            {/* ── Snackbar ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <div className={`inv-snackbar inv-snackbar--${snackbar.severity}`}>
                    {snackbar.message}
                </div>
            </Snackbar>
        </div>
    );
}
